import { factory, am } from './utils';

import httpClient from '@/utils/httpClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const GET_REPORTS = am(`GET_REPORTS`);
const SET_INVENTION = am(`SET_INVENTION`);
const CHOOSE_REPORTS = am(`CHOOSE_REPORTS`);
const GET_CONFIGURATION = am(`GET_CONFIGURATION`);
const GENERATE_REPORTS = am(`GENERATE_REPORTS`);
const GET_RESULTS = am(`GET_RESULTS`);

export default {
  namespaced: true,
  ...factory({
    state: {
      collection: [],
      reportsToGenerate: [],
      fieldsRequired: [],
      inention: {},
      results: [],
      jobs: [],
      fillDraft: null,
      chooseDocumentsRequest: null,
      pendingChosenFiles: null,
      isGetReportsRequestPending: false,
      isGetReportsRequestFailed: false,
      isGetConfigurationPending: false,
      isGetConfigurationFailed: false,
      isGenerateReportsPending: false,
      isGenerateReportsFailed: false,
      isGetResultsPending: false,
      isGetResultsFailed: false
    },
    mutations: {
      setFillDraft(state, draft) {
        state.fillDraft = draft;
      },
      setChooseDocumentsRequest(state, request) {
        state.chooseDocumentsRequest = request;
      },
      clearChooseDocumentsRequest(state) {
        state.chooseDocumentsRequest = null;
      },
      appendFilesToFillDraft(state, { fieldKey, files, removedDocumentIds = [] }) {
        if (!state.fillDraft) {
          state.fillDraft = {
            fieldValues: {},
            fieldFiles: {},
            useCurrentDocument: {},
            currentDocumentMeta: {},
            isPublic: false,
            shouldShowPublicOption: false
          };
        }
        const removed = removedDocumentIds.map(String);
        const existing = (state.fillDraft.fieldFiles?.[fieldKey] || []).filter(
          file => file.documentId == null || !removed.includes(String(file.documentId))
        );
        state.fillDraft = {
          ...state.fillDraft,
          fieldFiles: {
            ...state.fillDraft.fieldFiles,
            [fieldKey]: [...existing, ...files]
          }
        };
      },
      setPendingChosenFiles(state, payload) {
        state.pendingChosenFiles = payload;
      },
      clearPendingChosenFiles(state) {
        state.pendingChosenFiles = null;
      },
      [GET_REPORTS.FAILED](state) {
        state.isGetReportsRequestFailed = true;
        state.isGetReportsRequestPending = false;
      },
      [GET_REPORTS.STARTED](state) {
        state.isGetReportsRequestPending = true;
        state.isGetReportsRequestFailed = false;
      },
      [GET_REPORTS.COMPLETED](state, collection) {
        state.isGetReportsRequestPending = false;
        state.isGetReportsRequestFailed = false;
        state.collection = collection;
      },
      [SET_INVENTION.COMPLETED](state, invention) {
        state.invention = invention;
      },
      [CHOOSE_REPORTS.COMPLETED](state, reportIdentifiers) {
        state.reportsToGenerate = reportIdentifiers;
      },
      [GET_CONFIGURATION.STARTED](state) {
        state.isGetConfigurationPending = true;
        state.isGetConfigurationFailed = false;
      },
      [GET_CONFIGURATION.COMPLETED](state, fieldsRequired) {
        state.isGetConfigurationPending = false;
        state.isGetConfigurationFailed = false;
        state.fieldsRequired = fieldsRequired;
      },
      [GET_CONFIGURATION.FAILED](state) {
        state.isGetConfigurationFailed = true;
        state.isGetConfigurationPending = false;
      },
      [GENERATE_REPORTS.STARTED](state) {
        state.isGenerateReportsPending = true;
        state.isGenerateReportsFailed = false;
      },
      [GENERATE_REPORTS.COMPLETED](state, jobs) {
        state.isGenerateReportsPending = false;
        state.isGenerateReportsFailed = false;

        state.results = [...state.results, ...jobs];
      },
      [GENERATE_REPORTS.FAILED](state) {
        state.isGenerateReportsFailed = true;
        state.isGenerateReportsPending = false;
      },
      [GET_RESULTS.FAILED](state) {
        state.isGetResultsFailed = true;
        state.isGetResultsPending = false;
      },
      [GET_RESULTS.STARTED](state) {
        state.isGetResultsPending = true;
        state.isGetResultsFailed = false;
      },
      [GET_RESULTS.COMPLETED](state, res) {
        state.isGetResultsPending = false;
        state.isGetResultsFailed = false;
        state.jobs = res.data;
      }
    },
    actions: {
      setInvention({ commit }, invention) {
        commit(SET_INVENTION.COMPLETED, invention);
      },
      saveFillDraft({ commit }, draft) {
        commit('setFillDraft', draft);
      },
      clearFillDraft({ commit }) {
        commit('setFillDraft', null);
      },
      startChooseDocuments({ commit }, payload) {
        commit('setChooseDocumentsRequest', payload);
      },
      completeChooseDocuments({ commit }, { fieldKey, files, removedDocumentIds = [] }) {
        commit('appendFilesToFillDraft', { fieldKey, files, removedDocumentIds });
        commit('setPendingChosenFiles', { fieldKey, files, removedDocumentIds });
        commit('clearChooseDocumentsRequest');
      },
      cancelChooseDocuments({ commit }) {
        commit('clearChooseDocumentsRequest');
      },
      clearPendingChosenFiles({ commit }) {
        commit('clearPendingChosenFiles');
      },
      chooseReports({ commit }, reportIdentifiers) {
        commit(CHOOSE_REPORTS.COMPLETED, reportIdentifiers);
      },
      async get({ commit, state }) {
        try {
          commit(GET_REPORTS.STARTED);
          if (!state.invention?.id) {
            commit(GET_REPORTS.FAILED);
            return;
          }
          const reports = await httpClient.get(
            `/api/reports/?inventionId=${state.invention.id}&includeHidden=false`
          );

          commit(GET_REPORTS.COMPLETED, reports.data);
          return reports.data;
        } catch (e) {
          commit(GET_REPORTS.FAILED);
          throw e;
        }
      },
      /**
       * Fetch merged report form fields for selected reportIds.
       * Each field may include prompt-chain defaults used only by the UI to pre-fill:
       * - defaultValue?: string | boolean | ImageDefault[]  (presence-check; false/"" are valid)
       * - sourceFiles?: SourceFile[]  (attached text/file defaults; prefer over string defaultValue for uploads)
       * Generation does not apply these server-side — omitted submit fields stay missing.
       *
       * @returns {Promise<Array<{
       *   name: string,
       *   label?: string,
       *   type: string,
       *   optional?: boolean,
       *   identifiers: string[],
       *   mimes?: string[],
       *   maxFiles?: number|null,
       *   options?: string[],
       *   defaultValue?: string|boolean|Array<{id?: string, name: string, content?: string, type?: string, mimeType?: string, metadata?: object}>,
       *   sourceFiles?: Array<{name: string, content: string, type: string}>
       * }>>}
       */
      async getRequiredFields({ commit, state }) {
        try {
          commit(GET_CONFIGURATION.STARTED);
          if (!state.reportsToGenerate?.length) {
            commit(GET_CONFIGURATION.FAILED);
            return;
          }

          const queryString = state.reportsToGenerate.map(id => `reportIds=${id}`).join('&');
          const requiredFields = await httpClient.get(`/api/reports/config/merged?${queryString}`);

          commit(GET_CONFIGURATION.COMPLETED, requiredFields.fields);
          return requiredFields.fields;
        } catch (e) {
          commit(GET_CONFIGURATION.FAILED);
          throw e;
        }
      },
      async validateGenerate({ state }, formData) {
        if (!state.invention.id) {
          return;
        }
        return httpClient.request(`/api/reports/validate/${state.invention.id}`, {
          json: false,
          method: 'POST',
          body: formData
        });
      },
      async generate({ commit, state }, payload) {
        try {
          commit(GENERATE_REPORTS.STARTED);
          if (!state.invention.id) {
            commit(GENERATE_REPORTS.FAILED);
            return;
          }

          const formData = payload?.formData || payload;
          const isPublic = typeof payload?.isPublic === 'boolean' ? payload.isPublic : undefined;
          const query = typeof isPublic === 'boolean' ? `?isPublic=${isPublic}` : '';

          const generationJobs = await httpClient.request(`/api/reports/generate/${state.invention.id}${query}`, {
            json: false,
            method: 'POST',
            body: formData
          });

          commit(GENERATE_REPORTS.COMPLETED, generationJobs.results);
          return generationJobs.results;
        } catch (e) {
          commit(GENERATE_REPORTS.FAILED);
          throw e;
        }
      },
      async getResults({ commit, state }) {
        try {
          commit(GET_RESULTS.STARTED);
          if (!state.invention.id) {
            commit(GET_RESULTS.FAILED);
            return;
          }

          const result = await httpClient.get(`/api/reports/results/invention/${state.invention.id}`);

          commit(GET_RESULTS.COMPLETED, result);
          return result;
        } catch (e) {
          commit(GET_RESULTS.FAILED);
          throw e;
        }
      }
    }
  })('reports')
};
